import React, { useState, useMemo, memo, useCallback } from 'react';
import { LineChart as ChartIcon } from 'lucide-react';
import {
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import {
  BarTemplate,
  LineTemplate,
  ScatterTemplate,
  HistogramTemplate,
  SankeyTemplate,
  CHART_COLORS
} from './charts/ChartTemplates';

export const DynamicChart = memo(({ data, spec, noContainer = false }) => {
  const [hiddenSeries, setHiddenSeries] = useState(new Set());

  const getSortValue = useCallback((value) => {
    if (value === null || value === undefined) return { type: 'empty', value: '' };
    if (typeof value === 'number') return { type: 'number', value };

    const asNumber = Number(value);
    if (!Number.isNaN(asNumber) && value !== '') {
      return { type: 'number', value: asNumber };
    }

    if (value instanceof Date) {
      return { type: 'date', value: value.getTime() };
    }

    if (typeof value === 'string') {
      const t = Date.parse(value);
      if (!Number.isNaN(t)) return { type: 'date', value: t };
      return { type: 'string', value: value };
    }

    return { type: 'string', value: String(value) };
  }, []);

  const sortRowsByX = useCallback((rows, xKey) => {
    if (!Array.isArray(rows) || !xKey) return rows;
    if (rows.length < 2) return rows;

    // Fast path: avoid O(n log n) sort when rows are already ordered.
    let isAlreadySorted = true;
    for (let i = 1; i < rows.length; i++) {
      const prevSort = getSortValue(rows[i - 1][xKey]);
      const curSort = getSortValue(rows[i][xKey]);
      const typeOrder = { number: 0, date: 1, string: 2, empty: 3 };

      if (prevSort.type !== curSort.type) {
        if (typeOrder[prevSort.type] > typeOrder[curSort.type]) {
          isAlreadySorted = false;
          break;
        }
        continue;
      }

      if (prevSort.type === 'number' || prevSort.type === 'date') {
        if (prevSort.value > curSort.value) {
          isAlreadySorted = false;
          break;
        }
      } else if (
        String(prevSort.value).localeCompare(String(curSort.value), undefined, { numeric: true }) > 0
      ) {
        isAlreadySorted = false;
        break;
      }
    }

    if (isAlreadySorted) return rows;

    return [...rows].sort((a, b) => {
      const aSort = getSortValue(a[xKey]);
      const bSort = getSortValue(b[xKey]);

      const typeOrder = { number: 0, date: 1, string: 2, empty: 3 };
      if (aSort.type !== bSort.type) {
        return typeOrder[aSort.type] - typeOrder[bSort.type];
      }

      if (aSort.type === 'number' || aSort.type === 'date') {
        return aSort.value - bSort.value;
      }

      return String(aSort.value).localeCompare(String(bSort.value), undefined, { numeric: true });
    });
  }, [getSortValue]);

  const handleLegendClick = useCallback((o) => {
    const legendKey = o?.value ?? o?.dataKey;
    if (!legendKey) return;
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(legendKey)) next.delete(legendKey);
      else next.add(legendKey);
      return next;
    });
  }, []);

  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data) || !spec) return [];
    const { plot_type, x, y, grouping } = spec;

    if (plot_type === 'sankey') {
      // Process data for Sankey diagram: convert to nodes and links format
      // For sankey: x -> source, y -> target, grouping -> value
      if (!x || !y || !grouping) return { nodes: [], links: [] };

      const nodeMap = new Map();
      let nodeIndex = 0;

      // First pass: collect all unique nodes
      data.forEach(row => {
        const sourceVal = String(row[x] || '');
        const targetVal = String(row[y] || '');

        if (sourceVal && !nodeMap.has(sourceVal)) {
          nodeMap.set(sourceVal, nodeIndex++);
        }
        if (targetVal && !nodeMap.has(targetVal)) {
          nodeMap.set(targetVal, nodeIndex++);
        }
      });

      // Create nodes array
      const nodes = Array.from(nodeMap.keys()).map(name => ({ name }));

      // Create links array
      const links = data
        .filter(row => {
          const sourceVal = String(row[x] || '');
          const targetVal = String(row[y] || '');
          const val = Number(row[grouping]) || 0;
          return sourceVal && targetVal && val > 0;
        })
        .map(row => {
          const sourceVal = String(row[x] || '');
          const targetVal = String(row[y] || '');
          const val = Number(row[grouping]) || 0;
          return {
            source: nodeMap.get(sourceVal),
            target: nodeMap.get(targetVal),
            value: val
          };
        });

      return { nodes, links };
    }

    if (plot_type === 'hist') {
      const col = y ?? x; // y is the histogram column per spec; x is fallback
      if (!col) return [];
      const allValues = data.map(r => {
        const v = r[col];
        if (typeof v === 'number') return isNaN(v) ? undefined : v;
        if (typeof v === 'string' && v.trim() !== '') { const n = Number(v); return isNaN(n) ? undefined : n; }
        return undefined;
      }).filter(v => v !== undefined);
      if (allValues.length === 0) return [];

      let min = Infinity;
      let max = -Infinity;
      for (const v of allValues) {
        if (v < min) min = v;
        if (v > max) max = v;
      }

      if (min === Infinity || max === -Infinity) return [];

      const isInteger = allValues.every(v => Number.isInteger(v));
      const range = max - min;
      const n = allValues.length;

      // Freedman-Diaconis rule: binWidth = 2 * IQR * n^(-1/3)
      // Falls back to Sturges' rule (ceil(log2(n)+1)) when IQR is 0
      const sorted = [...allValues].sort((a, b) => a - b);
      const q1 = sorted[Math.floor(n * 0.25)];
      const q3 = sorted[Math.floor(n * 0.75)];
      const iqr = q3 - q1;

      let binCount, binWidth;

      if (isInteger && range < 25) {
        // Discrete integers with small range: one bin per value
        binCount = range + 1;
        binWidth = 1;
      } else if (iqr > 0) {
        // Freedman-Diaconis
        binWidth = 2 * iqr * Math.pow(n, -1 / 3);
        if (isInteger) binWidth = Math.max(1, Math.ceil(binWidth));
        binCount = Math.ceil(range / binWidth);
      } else {
        // Sturges fallback (degenerate IQR)
        binCount = Math.ceil(Math.log2(n) + 1);
        binWidth = range / binCount || 1;
        if (isInteger) binWidth = Math.max(1, Math.ceil(binWidth));
      }

      // Sanity clamp
      binCount = Math.min(50, Math.max(3, binCount));

      const makeBinLabel = (binMin) => {
        if (isInteger) return `${Math.round(binMin)}`;
        return `${binMin.toFixed(2)}`;
      };

      if (grouping) {
        // Comparison histogram: bin per group
        const groups = [...new Set(data.map(r => String(r[grouping] || 'Unknown')))];

        const bins = Array.from({ length: binCount }, (_, i) => {
          const binMin = min + i * binWidth;
          const entry = { bin: makeBinLabel(binMin), _sort: binMin };
          groups.forEach(g => { entry[g] = 0; });
          return entry;
        });

        data.forEach(row => {
          const raw = row[col];
          const v = typeof raw === 'number' ? raw : Number(raw);
          if (isNaN(v)) return;
          let binIdx = Math.floor((v - min) / binWidth);
          if (binIdx >= binCount) binIdx = binCount - 1;
          if (binIdx < 0) binIdx = 0;
          const g = String(row[grouping] || 'Unknown');
          bins[binIdx][g]++;
        });

        return bins;
      }

      // Simple (non-grouped) histogram
      const bins = Array.from({ length: binCount }, (_, i) => {
        const binMin = min + i * binWidth;
        return {
          bin: makeBinLabel(binMin),
          count: 0,
          _sort: binMin
        };
      });

      allValues.forEach(v => {
        let binIdx = Math.floor((v - min) / binWidth);
        if (binIdx >= binCount) binIdx = binCount - 1;
        if (binIdx < 0) binIdx = 0;
        bins[binIdx].count++;
      });

      return bins;
    }

    if (plot_type === 'scatter') {
      // Scatter should preserve row-level points; each row is a point.
      const cleaned = data.filter(row => row && row[x] !== undefined && row[y] !== undefined);
      return sortRowsByX(cleaned, x);
    }

    if (!grouping) {
      // Keep x-axis deterministic for charts rendered on an axis.
      if (plot_type === 'line' || plot_type === 'bar') {
        return sortRowsByX(data, x);
      }
      return data;
    }

    // Transform long format to wide format for grouping
    const pivoted = data.reduce((acc, row) => {
      const xVal = row[x];
      const groupVal = row[grouping];
      const yVal = row[y];

      if (xVal === undefined || xVal === null) return acc;

      let existing = acc.find(r => String(r[x]) === String(xVal));
      if (!existing) {
        existing = { [x]: xVal };
        acc.push(existing);
      }
      if (groupVal !== undefined && groupVal !== null) {
        existing[groupVal] = yVal;
      }
      return acc;
    }, []);

    // Sort by X axis
    return sortRowsByX(pivoted, x);
  }, [data, spec, sortRowsByX]);

  const series = useMemo(() => {
    if (!data || !Array.isArray(data) || !spec) return [];
    const { y, y_columns, grouping } = spec;
    if (grouping) {
      return [...new Set(data.map(row => String(row[grouping] || 'Unknown')))];
    }
    return y_columns || [y];
  }, [data, spec]);

  const scatterSeriesData = useMemo(() => {
    if (!spec || spec.plot_type !== 'scatter' || !Array.isArray(processedData)) return null;
    if (!spec.grouping) return null;

    const grouped = {};
    for (const row of processedData) {
      const key = String(row[spec.grouping] || 'Unknown');
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    }
    return grouped;
  }, [processedData, spec]);

  if (!data || !spec) return null;

  const { plot_type, x, title, xlabel, ylabel } = spec;

  const renderChart = () => {
    // Handle empty data for all chart types
    const isEmpty = plot_type === 'sankey'
      ? (!processedData || !processedData.nodes || processedData.nodes.length === 0 || !processedData.links || processedData.links.length === 0)
      : (!processedData || processedData.length === 0);

    if (isEmpty) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <ChartIcon size={24} className="mb-2 opacity-20" />
          <p className="text-[10px] font-bold uppercase tracking-widest">No data to display</p>
        </div>
      );
    }

    // Sankey doesn't need axes
    if (plot_type === 'sankey') {
      return <SankeyTemplate data={processedData} />;
    }

    const xAxisKey = plot_type === 'hist' ? 'bin' : x;
    const seriesKeys = plot_type === 'hist'
      ? (spec.grouping
        ? [...new Set(data.map(row => String(row[spec.grouping] || 'Unknown')))]
        : ['count'])
      : series;

    const xAxis = (
      <XAxis
        dataKey={xAxisKey}
        axisLine={false}
        tickLine={false}
        tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
        dy={5}
        label={xlabel ? {
          value: xlabel,
          position: 'insideBottom',
          offset: -10,
          fontSize: 9,
          fontWeight: 800,
          fill: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        } : null}
      />
    );

    const yAxis = (
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
        dx={-5}
        label={ylabel ? {
          value: ylabel,
          angle: -90,
          position: 'insideLeft',
          offset: -30,
          fontSize: 9,
          fontWeight: 800,
          fill: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        } : null}
      />
    );

    const templateProps = {
      data: processedData,
      x: xAxisKey,
      series: seriesKeys,
      spec,
      scatterSeriesData,
      xAxis,
      yAxis,
      colors: CHART_COLORS,
      hiddenSeries,
      handleLegendClick
    };

    if (plot_type === 'bar') return <BarTemplate {...templateProps} />;
    if (plot_type === 'line') return <LineTemplate {...templateProps} />;
    if (plot_type === 'scatter') return <ScatterTemplate {...templateProps} />;
    if (plot_type === 'hist') return <HistogramTemplate {...templateProps} />;

    return <div className="text-center p-4 text-gray-400 text-xs">Unsupported plot type: {plot_type}</div>;
  };

  const chart = (
    <ResponsiveContainer width="100%" height="100%" minHeight={0} minWidth={0}>
      {renderChart()}
    </ResponsiveContainer>
  );

  if (noContainer) return chart;

  return (
    <div className="h-56 w-full mt-4 p-2 bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
      <h4 className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2 text-center">{title}</h4>
      {chart}
    </div>
  );
});
